/**
 * Delta-based Undo/Redo Manager
 * 
 * Instead of storing the entire floorplan state on every change,
 * this system tracks only the objects that were modified and stores
 * their previous values. This makes undo/redo O(changes) instead of O(total objects).
 */

import { makeAutoObservable, runInAction } from 'mobx';
import type { WallType, SymbolType, SingleLineType, RulerLineType, Ruler3DType, RoomType, GroupType } from '../types/floorplannerTypes';
import * as THREE from 'three';
import { serializeVector2 } from '../utils/vector2Serialization';

// Types of changes we can track
export type ChangeType = 'add' | 'modify' | 'delete';

// A single object change record
export interface ObjectChange {
    type: ChangeType;
    objectType: 'wall' | 'symbol' | 'singleLine' | 'rulerLine' | 'ruler3D' | 'room' | 'group';
    objectId: string;
    // For 'modify' and 'delete': the previous state of the object (serialized)
    previousState?: string;
    // For 'add' and 'modify': the new state of the object (serialized)
    newState?: string;
}

// A group of changes made in a single "transaction" (e.g., single user action)
export interface UndoRedoEntry {
    timestamp: number;
    changes: ObjectChange[];
    description?: string;
}

const UNDO_REDO_LIMIT = 50;

export class UndoRedoManager {
    undoStack: UndoRedoEntry[] = [];
    redoStack: UndoRedoEntry[] = [];

    // Track the current transaction (changes being accumulated)
    private currentTransaction: ObjectChange[] | null = null;
    private transactionDescription: string | undefined;

    // Snapshot of objects before current transaction started (for detecting changes)
    private preTransactionSnapshots: Map<string, string> = new Map();

    constructor() {
        makeAutoObservable(this);
    }

    get canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    get canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    /** True while beginTransaction has been called and endTransaction has not. */
    get isTrackingTransaction(): boolean {
        return this.currentTransaction !== null;
    }

    get hasActiveTransaction(): boolean {
        return this.currentTransaction !== null && this.currentTransaction.length > 0;
    }

    /**
     * Begin a new transaction. All changes made until endTransaction() is called
     * will be grouped together as a single undo/redo entry.
     */
    beginTransaction(description?: string): void {
        if (this.currentTransaction !== null) {
            // Already in a transaction - this is okay, we'll accumulate changes
            return;
        }
        this.currentTransaction = [];
        this.transactionDescription = description;
        this.preTransactionSnapshots.clear();
    }

    /**
     * Record that an object is about to be modified.
     * Call this BEFORE making changes to capture the previous state.
     */
    recordObjectState(
        objectType: ObjectChange['objectType'],
        objectId: string,
        currentObject: WallType | SymbolType | SingleLineType | RulerLineType | Ruler3DType | RoomType | GroupType | null
    ): void {
        if (!this.currentTransaction) {
            // Auto-start a transaction if not in one
            this.beginTransaction();
        }

        // Only capture snapshot once per object per transaction
        const key = `${objectType}:${objectId}`;
        if (!this.preTransactionSnapshots.has(key)) {
            const snapshot = currentObject ? this.serializeObject(objectType, currentObject) : null;
            this.preTransactionSnapshots.set(key, snapshot ?? '');
        }
    }

    /**
     * Record a change to an object.
     * Call this AFTER making changes to record the new state.
     */
    recordChange(
        type: ChangeType,
        objectType: ObjectChange['objectType'],
        objectId: string,
        newObject: WallType | SymbolType | SingleLineType | RulerLineType | Ruler3DType | RoomType | GroupType | null
    ): void {
        if (!this.currentTransaction) {
            // Auto-start a transaction if not in one
            this.beginTransaction();
        }

        const key = `${objectType}:${objectId}`;
        const previousState = this.preTransactionSnapshots.get(key);
        const newState = newObject ? this.serializeObject(objectType, newObject) : undefined;

        // Check if there's already a change for this object in the current transaction
        const existingIndex = this.currentTransaction!.findIndex(
            c => c.objectType === objectType && c.objectId === objectId
        );

        if (existingIndex >= 0) {
            // Update existing change - merge with previous
            const existing = this.currentTransaction![existingIndex];

            if (type === 'delete' && existing.type === 'add') {
                // Object was added then deleted - cancel out, remove the change
                this.currentTransaction!.splice(existingIndex, 1);
                return;
            }

            // Keep the original previousState, update newState
            existing.newState = newState;
            if (type === 'delete') {
                existing.type = existing.type === 'add' ? 'add' : 'delete';
            }
        } else {
            // New change for this object
            this.currentTransaction!.push({
                type,
                objectType,
                objectId,
                previousState: previousState || undefined,
                newState,
            });
        }
    }

    /**
     * End the current transaction and push it to the undo stack.
     */
    endTransaction(): void {
        if (!this.currentTransaction || this.currentTransaction.length === 0) {
            // No changes recorded
            this.currentTransaction = null;
            this.transactionDescription = undefined;
            this.preTransactionSnapshots.clear();
            return;
        }

        // Filter out no-op changes (where previous and new state are identical)
        const meaningfulChanges = this.currentTransaction.filter(change => {
            if (change.type === 'add' || change.type === 'delete') {
                return true;
            }
            // For modifications, check if anything actually changed
            return change.previousState !== change.newState;
        });

        if (meaningfulChanges.length === 0) {
            this.currentTransaction = null;
            this.transactionDescription = undefined;
            this.preTransactionSnapshots.clear();
            return;
        }

        // Clear redo stack when new changes are made
        this.redoStack = [];

        // Create the undo entry
        const entry: UndoRedoEntry = {
            timestamp: Date.now(),
            changes: meaningfulChanges,
            description: this.transactionDescription,
        };

        this.undoStack.push(entry);

        // Enforce limit
        if (this.undoStack.length > UNDO_REDO_LIMIT) {
            this.undoStack.shift();
        }

        // Clean up
        this.currentTransaction = null;
        this.transactionDescription = undefined;
        this.preTransactionSnapshots.clear();
    }

    /**
     * Cancel the current transaction without saving changes.
     */
    cancelTransaction(): void {
        this.currentTransaction = null;
        this.transactionDescription = undefined;
        this.preTransactionSnapshots.clear();
    }

    /**
     * Remove undo/redo changes that reference an object managed by a transient
     * interaction, such as a wall-construction preview or a construction-local
     * undo step. This keeps the global undo history from restoring objects the
     * interaction already intentionally discarded.
     */
    pruneObjectChanges(
        objectType: ObjectChange['objectType'],
        objectId: string,
        options?: { undoStackEntriesBefore?: number }
    ): void {
        const pruneStack = (
            stack: UndoRedoEntry[],
            removeEntriesFromIndex?: number,
        ) => stack
            .filter((_entry, index) => removeEntriesFromIndex === undefined || index < removeEntriesFromIndex)
            .map(entry => ({
                ...entry,
                changes: entry.changes.filter(change =>
                    !(change.objectType === objectType && change.objectId === objectId)
                ),
            }))
            .filter(entry => entry.changes.length > 0);

        this.undoStack = pruneStack(this.undoStack, options?.undoStackEntriesBefore);
        this.redoStack = pruneStack(this.redoStack);

        if (this.currentTransaction) {
            this.currentTransaction = this.currentTransaction.filter(change =>
                !(change.objectType === objectType && change.objectId === objectId)
            );
        }
        this.preTransactionSnapshots.delete(`${objectType}:${objectId}`);
    }

    /**
     * Undo the last transaction.
     * Returns the list of affected object IDs for targeted updates.
     */
    undo(
        applyChange: (change: ObjectChange, isUndo: true) => void
    ): string[] {
        const entry = this.undoStack.pop();
        if (!entry) {
            return [];
        }

        const affectedIds: string[] = [];

        // Apply changes in reverse order. Keep the original entry on the redo
        // stack — applyUndoRedoChange uses isUndo to choose previous vs new state
        // and to invert add/delete. Swapping types/states here broke redo for
        // adds (library symbols, walls) and modifies.
        runInAction(() => {
            for (let i = entry.changes.length - 1; i >= 0; i--) {
                const change = entry.changes[i];
                applyChange(change, true);
                affectedIds.push(change.objectId);
            }
        });

        this.redoStack.push(entry);

        return affectedIds;
    }

    /**
     * Redo the last undone transaction.
     * Returns the list of affected object IDs for targeted updates.
     */
    redo(
        applyChange: (change: ObjectChange, isUndo: false) => void
    ): string[] {
        const entry = this.redoStack.pop();
        if (!entry) {
            return [];
        }

        const affectedIds: string[] = [];

        // Apply the original entry forward (isUndo=false restores newState / re-adds).
        runInAction(() => {
            for (const change of entry.changes) {
                applyChange(change, false);
                affectedIds.push(change.objectId);
            }
        });

        this.undoStack.push(entry);

        return affectedIds;
    }

    /**
     * Reset both stacks (e.g., when loading a new floorplan).
     */
    reset(): void {
        this.undoStack = [];
        this.redoStack = [];
        this.currentTransaction = null;
        this.transactionDescription = undefined;
        this.preTransactionSnapshots.clear();
    }

    /**
     * Serialize an object to JSON string for storage.
     */
    private serializeObject(
        objectType: ObjectChange['objectType'],
        obj: WallType | SymbolType | SingleLineType | RulerLineType | Ruler3DType | RoomType | GroupType
    ): string {
        // Create a clean copy without runtime-only properties
        const copy = { ...obj };

        // Remove properties that shouldn't be stored
        delete (copy as any).selected;
        delete (copy as any).lastUpdate;

        // Convert Vector2 instances to plain objects
        if ('start' in copy && copy.start instanceof THREE.Vector2) {
            (copy as any).start = { x: copy.start.x, y: copy.start.y };
        }
        if ('start' in copy && copy.start instanceof THREE.Vector3) {
            (copy as any).start = { x: copy.start.x, y: copy.start.y, z: copy.start.z };
        }
        if ('end' in copy && copy.end instanceof THREE.Vector2) {
            (copy as any).end = { x: copy.end.x, y: copy.end.y };
        }
        if ('end' in copy && copy.end instanceof THREE.Vector3) {
            (copy as any).end = { x: copy.end.x, y: copy.end.y, z: copy.end.z };
        }
        if ('controlPoint' in copy && copy.controlPoint instanceof THREE.Vector2) {
            (copy as any).controlPoint = { x: copy.controlPoint.x, y: copy.controlPoint.y };
        }
        if ('position' in copy && copy.position instanceof THREE.Vector2) {
            (copy as any).position = { x: copy.position.x, y: copy.position.y };
        }
        if ('centroid' in copy && copy.centroid instanceof THREE.Vector2) {
            (copy as any).centroid = { x: copy.centroid.x, y: copy.centroid.y };
        }
        if ('vertices' in copy && Array.isArray((copy as any).vertices)) {
            (copy as any).vertices = (copy as any).vertices.map((vertex: any) => serializeVector2(vertex));
        }

        // Handle edge connections
        if ('edgeConnections' in copy && Array.isArray(copy.edgeConnections)) {
            (copy as any).edgeConnections = copy.edgeConnections.map((conn: any) => ({
                ...conn,
                sourceEdgePoint: serializeVector2(conn.sourceEdgePoint),
                targetEdgePoint: serializeVector2(conn.targetEdgePoint),
            }));
        }

        // Handle cutouts Set -> Array conversion
        if ('cutouts' in copy && Array.isArray(copy.cutouts)) {
            (copy as any).cutouts = copy.cutouts.map((cutout: any) => ({
                ...cutout,
                appliesTo: Array.isArray(cutout.appliesTo) ? cutout.appliesTo :
                    cutout.appliesTo instanceof Set ? Array.from(cutout.appliesTo) : [],
            }));
        }

        return JSON.stringify(copy);
    }
}

// Singleton instance
export const undoRedoManager = new UndoRedoManager();
